import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { courseAPI, learningAssetAPI, assetQuizAPI } from '../services/api';

function StudentCourseProgress() {
  const { courseId, studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [togglingAsset, setTogglingAsset] = useState(null);
  const [assetQuizSubmissions, setAssetQuizSubmissions] = useState({});
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedQuizSubmission, setSelectedQuizSubmission] = useState(null);
  const [loadingQuizDetails, setLoadingQuizDetails] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/courses');
      return;
    }
    fetchStudentProgress();
  }, [courseId, studentId, user, navigate]);

  const fetchStudentProgress = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getStudentProgress(courseId, studentId);
      setData(response.data);
      setError('');
      
      // Fetch asset quiz submissions for each asset
      const assets = response.data.assets || [];
      const submissions = {};
      
      await Promise.all(
        assets.map(async (asset) => {
          try {
            const quizResponse = await assetQuizAPI.getAssetSubmissions(asset._id, studentId);
            if (quizResponse.data.submissions && quizResponse.data.submissions.length > 0) {
              submissions[asset._id] = quizResponse.data.submissions;
            }
          } catch (err) {
            // No submissions or quiz for this asset, which is fine
          }
        })
      );
      
      setAssetQuizSubmissions(submissions);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch student progress');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCompletion = async (assetId, isCurrentlyCompleted) => {
    try {
      setTogglingAsset(assetId);
      
      if (isCurrentlyCompleted) {
        // Mark as incomplete
        await learningAssetAPI.adminMarkAssetIncomplete(assetId, studentId);
      } else {
        // Mark as complete
        await learningAssetAPI.adminMarkAssetComplete(assetId, studentId);
      }
      
      // Refresh the data
      await fetchStudentProgress();
    } catch (err) {
      console.error('Error toggling completion:', err);
      alert(err.response?.data?.message || 'Failed to update completion status');
    } finally {
      setTogglingAsset(null);
    }
  };

  const handleViewQuizAttempt = async (submissionId) => {
    try {
      setLoadingQuizDetails(true);
      setShowQuizModal(true);
      
      const response = await assetQuizAPI.getSubmission(submissionId);
      setSelectedQuizSubmission(response.data);
    } catch (err) {
      console.error('Error fetching quiz details:', err);
      alert(err.response?.data?.message || 'Failed to fetch quiz details');
      setShowQuizModal(false);
    } finally {
      setLoadingQuizDetails(false);
    }
  };

  const closeQuizModal = () => {
    setShowQuizModal(false);
    setSelectedQuizSubmission(null);
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#667eea', fontSize: '16px' }}>Loading student progress...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div className="error">{error}</div>
          <button 
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <p>No data available</p>
          <button 
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const { course, student, enrollment, assets, chapters, quizSubmissions } = data;

  const getAssetStatusIcon = (asset) => {
    const isCompleted = enrollment?.progress?.completedAssets?.some(ca => 
      (ca.assetId === asset._id || ca.assetId?._id === asset._id)
    );
    
    if (isCompleted) {
      return <span style={{ color: '#48bb78', fontSize: '18px' }}>✓</span>;
    }
    return <span style={{ color: '#cbd5e0', fontSize: '18px' }}>○</span>;
  };

  const isAssetCompleted = (asset) => {
    return enrollment?.progress?.completedAssets?.some(ca => 
      (ca.assetId === asset._id || ca.assetId?._id === asset._id)
    );
  };

  const getAssetScore = (asset) => {
    const completedAsset = enrollment?.progress?.completedAssets?.find(ca => 
      (ca.assetId === asset._id || ca.assetId?._id === asset._id)
    );
    
    if (completedAsset && completedAsset.score !== undefined && completedAsset.score !== null) {
      return completedAsset.score;
    }
    return null;
  };

  const getAssetCompletionTime = (asset) => {
    const completedAsset = enrollment?.progress?.completedAssets?.find(ca => 
      (ca.assetId === asset._id || ca.assetId?._id === asset._id)
    );
    
    if (completedAsset && completedAsset.completedAt) {
      return new Date(completedAsset.completedAt);
    }
    return null;
  };

  const formatCompletionTime = (date) => {
    if (!date) return null;
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    // If less than 24 hours, show relative time
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    
    // Otherwise show date
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const getQuizSubmission = (assetId) => {
    // Find if this asset references a quiz
    const asset = assets.find(a => a._id === assetId);
    if (!asset || asset.assetType !== 'quiz' || !asset.contentReference?.refId) {
      return null;
    }
    
    // Find the quiz submission for this quiz
    const quizId = asset.contentReference.refId;
    return quizSubmissions?.find(sub => 
      (sub.quiz === quizId || sub.quiz?._id === quizId)
    );
  };

  const getVideoProgress = (assetId) => {
    return enrollment?.progress?.videoProgress?.find(vp => 
      (vp.assetId === assetId || vp.assetId?._id === assetId)
    );
  };

  const getAssetQuizSubmissions = (assetId) => {
    return assetQuizSubmissions[assetId] || [];
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container" style={{ paddingTop: '20px' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <button 
              onClick={() => navigate(`/courses/${courseId}/chapters/progress`)}
              className="btn btn-secondary"
              style={{ marginBottom: '12px', fontSize: '13px', padding: '6px 12px' }}
            >
              ← Back to Course Progress
            </button>
            <h2 style={{ color: '#667eea', fontSize: '24px', marginBottom: '8px' }}>
              {student?.name}'s Progress
            </h2>
            <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
              Course: {course?.title} ({course?.code})
            </p>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <div style={{ 
              padding: '12px 20px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              color: '#fff'
            }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '12px', opacity: 0.9 }}>Overall Progress</p>
              <p style={{ margin: 0, fontSize: '28px', fontWeight: '700' }}>
                {enrollment?.progress?.percentComplete || 0}%
              </p>
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#718096' }}>
              {enrollment?.progress?.completedAssetsCount || 0} of {enrollment?.progress?.totalAssetsCount || 0} completed
            </p>
          </div>
        </div>
      </div>

      {/* Enrollment Info */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#1a202c' }}>
          Enrollment Details
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>Status</p>
            <p style={{ 
              margin: 0, 
              fontSize: '14px', 
              fontWeight: '600',
              color: enrollment?.status === 'completed' ? '#48bb78' : '#667eea'
            }}>
              {enrollment?.status || 'N/A'}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>Enrolled On</p>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a202c' }}>
              {enrollment?.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>Enrollment Source</p>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a202c' }}>
              {enrollment?.enrollmentSource || 'N/A'}
            </p>
          </div>
          {enrollment?.completedAt && (
            <div>
              <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>Completed On</p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#48bb78' }}>
                {new Date(enrollment.completedAt).toLocaleDateString()}
              </p>
            </div>
          )}
          {enrollment?.lastAccessedAt && (
            <div>
              <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>Last Accessed</p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a202c' }}>
                {new Date(enrollment.lastAccessedAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Assets Progress by Chapter */}
      <div className="card">
        <h3 style={{ fontSize: '18px', marginBottom: '20px', color: '#1a202c' }}>
          Assets Progress
        </h3>

        {chapters && chapters.length > 0 ? (
          chapters
            .filter(ch => !ch.parentChapter)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(chapter => {
              const chapterAssets = assets
                .filter(a => a.chapter?._id === chapter._id || a.chapter === chapter._id)
                .sort((a, b) => (a.order || 0) - (b.order || 0));
              
              if (chapterAssets.length === 0) return null;

              return (
                <div 
                  key={chapter._id} 
                  style={{ 
                    marginBottom: '24px',
                    padding: '16px',
                    background: '#f7fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '16px', 
                    color: '#2d3748',
                    fontWeight: '600'
                  }}>
                    📖 {chapter.title}
                  </h4>
                  
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {chapterAssets.map(asset => {
                      const score = getAssetScore(asset);
                      const status = getAssetStatusIcon(asset);
                      const completionTime = getAssetCompletionTime(asset);
                      const quizSubmission = getQuizSubmission(asset._id);
                      const videoProgress = getVideoProgress(asset._id);
                      const assetQuizSubs = getAssetQuizSubmissions(asset._id);

                      return (
                        <div 
                          key={asset._id}
                          style={{
                            padding: '12px 16px',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                            {status}
                            <div style={{ flex: 1 }}>
                              <h5 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#1a202c' }}>
                                {asset.assetType === 'video' && '�'}
                                {asset.assetType === 'document' && '📄'}
                                {asset.assetType === 'quiz' && '📝'}
                                {asset.assetType === 'flashcard' && '🎴'}
                                {asset.assetType === 'interview' && '🎤'}
                                {asset.assetType === 'link' && '🔗'}
                                {asset.assetType === 'note' && '📋'}
                                {asset.assetType === 'transcript' && '📝'}
                                {' '}{asset.title}
                              </h5>
                              <p style={{ margin: 0, fontSize: '12px', color: '#718096' }}>
                                Type: {asset.assetType}
                                {asset.isRequired && (
                                  <span style={{ 
                                    marginLeft: '8px', 
                                    padding: '2px 6px', 
                                    background: '#fee2e2', 
                                    color: '#991b1b',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}>
                                    Required
                                  </span>
                                )}
                              </p>
                              {completionTime && (
                                <p style={{ 
                                  margin: '4px 0 0 0', 
                                  fontSize: '11px', 
                                  color: '#48bb78',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  ✓ Completed {formatCompletionTime(completionTime)}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div style={{ 
                            textAlign: 'right',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            alignItems: 'flex-end'
                          }}>
                            {asset.assetType === 'video' && videoProgress ? (
                              <div style={{ 
                                padding: '8px 12px',
                                background: '#e6f2ff',
                                borderRadius: '8px',
                                minWidth: '120px'
                              }}>
                                <p style={{ 
                                  margin: '0 0 2px 0', 
                                  fontSize: '11px', 
                                  color: '#0050b3'
                                }}>
                                  Watch Progress
                                </p>
                                <p style={{ 
                                  margin: 0, 
                                  fontSize: '16px', 
                                  fontWeight: '700',
                                  color: '#0050b3'
                                }}>
                                  {videoProgress.percentWatched || 0}%
                                </p>
                                <p style={{ 
                                  margin: '2px 0 0 0', 
                                  fontSize: '10px', 
                                  color: '#1890ff'
                                }}>
                                  {formatDuration(videoProgress.watchedSeconds)} / {formatDuration(videoProgress.totalDuration)}
                                </p>
                              </div>
                            ) : asset.assetType === 'quiz' && quizSubmission ? (
                              <div style={{ 
                                padding: '8px 12px',
                                background: quizSubmission.score >= 70 ? '#c6f6d5' : '#fed7d7',
                                borderRadius: '8px'
                              }}>
                                <p style={{ 
                                  margin: '0 0 2px 0', 
                                  fontSize: '11px', 
                                  color: quizSubmission.score >= 70 ? '#22543d' : '#742a2a'
                                }}>
                                  Quiz Score
                                </p>
                                <p style={{ 
                                  margin: 0, 
                                  fontSize: '18px', 
                                  fontWeight: '700',
                                  color: quizSubmission.score >= 70 ? '#22543d' : '#742a2a'
                                }}>
                                  {quizSubmission.score}%
                                </p>
                                <p style={{ 
                                  margin: '2px 0 0 0', 
                                  fontSize: '10px', 
                                  color: quizSubmission.score >= 70 ? '#276749' : '#9b2c2c'
                                }}>
                                  {quizSubmission.correctAnswers} / {quizSubmission.totalQuestions}
                                </p>
                              </div>
                            ) : score !== null ? (
                              <div style={{ 
                                padding: '6px 12px',
                                background: '#e6f2ff',
                                borderRadius: '6px'
                              }}>
                                <p style={{ margin: 0, fontSize: '12px', color: '#0050b3' }}>
                                  Score: {score}%
                                </p>
                              </div>
                            ) : null}
                            
                            {/* Asset Quiz Submissions (for note/markdown assets) */}
                            {assetQuizSubs.length > 0 && (
                              <div style={{ 
                                padding: '12px',
                                background: '#fef3c7',
                                borderRadius: '8px',
                                border: '1px solid #fbbf24'
                              }}>
                                <p style={{ 
                                  margin: '0 0 8px 0', 
                                  fontSize: '11px', 
                                  fontWeight: '600',
                                  color: '#92400e'
                                }}>
                                  📝 Quiz Attempts ({assetQuizSubs.length}/3)
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  {assetQuizSubs.map((sub, idx) => (
                                    <div 
                                      key={sub._id}
                                      onClick={() => handleViewQuizAttempt(sub._id)}
                                      style={{
                                        padding: '6px 8px',
                                        background: sub.score >= 70 ? '#d1fae5' : '#fee2e2',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateX(4px)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateX(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                      }}
                                    >
                                      <span style={{ 
                                        fontSize: '10px', 
                                        color: sub.score >= 70 ? '#065f46' : '#991b1b',
                                        fontWeight: '600'
                                      }}>
                                        Attempt #{sub.attemptNumber}
                                      </span>
                                      <span style={{ 
                                        fontSize: '12px', 
                                        fontWeight: '700',
                                        color: sub.score >= 70 ? '#065f46' : '#991b1b'
                                      }}>
                                        {sub.score}%
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Toggle Completion Button */}
                            <button
                              onClick={() => handleToggleCompletion(asset._id, isAssetCompleted(asset))}
                              disabled={togglingAsset === asset._id}
                              style={{
                                padding: '6px 12px',
                                background: isAssetCompleted(asset) ? '#fed7d7' : '#c6f6d5',
                                color: isAssetCompleted(asset) ? '#742a2a' : '#22543d',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: togglingAsset === asset._id ? 'not-allowed' : 'pointer',
                                opacity: togglingAsset === asset._id ? 0.6 : 1,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => {
                                if (togglingAsset !== asset._id) {
                                  e.currentTarget.style.opacity = '0.8';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (togglingAsset !== asset._id) {
                                  e.currentTarget.style.opacity = '1';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }
                              }}
                            >
                              {togglingAsset === asset._id ? (
                                '⏳ Updating...'
                              ) : isAssetCompleted(asset) ? (
                                '✗ Mark Incomplete'
                              ) : (
                                '✓ Mark Complete'
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
        ) : null}

        {/* Course-level assets (no chapter) */}
        {assets && assets.filter(a => !a.chapter).length > 0 && (
          <div 
            style={{ 
              marginBottom: '24px',
              padding: '16px',
              background: '#f7fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}
          >
            <h4 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '16px', 
              color: '#2d3748',
              fontWeight: '600'
            }}>
              📚 Course-Level Assets
            </h4>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              {assets
                .filter(a => !a.chapter)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(asset => {
                  const score = getAssetScore(asset);
                  const status = getAssetStatusIcon(asset);
                  const completionTime = getAssetCompletionTime(asset);
                  const quizSubmission = getQuizSubmission(asset._id);
                  const videoProgress = getVideoProgress(asset._id);
                  const assetQuizSubs = getAssetQuizSubmissions(asset._id);

                  return (
                    <div 
                      key={asset._id}
                      style={{
                        padding: '12px 16px',
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        {status}
                        <div style={{ flex: 1 }}>
                          <h5 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#1a202c' }}>
                            {asset.assetType === 'video' && '🎥'}
                            {asset.assetType === 'document' && '📄'}
                            {asset.assetType === 'quiz' && '📝'}
                            {asset.assetType === 'flashcard' && '🎴'}
                            {asset.assetType === 'interview' && '🎤'}
                            {asset.assetType === 'link' && '🔗'}
                            {asset.assetType === 'note' && '📋'}
                            {asset.assetType === 'transcript' && '📝'}
                            {' '}{asset.title}
                          </h5>
                          <p style={{ margin: 0, fontSize: '12px', color: '#718096' }}>
                            Type: {asset.assetType}
                            {asset.isRequired && (
                              <span style={{ 
                                marginLeft: '8px', 
                                padding: '2px 6px', 
                                background: '#fee2e2', 
                                color: '#991b1b',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                Required
                              </span>
                            )}
                          </p>
                          {completionTime && (
                            <p style={{ 
                              margin: '4px 0 0 0', 
                              fontSize: '11px', 
                              color: '#48bb78',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              ✓ Completed {formatCompletionTime(completionTime)}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ 
                        textAlign: 'right',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        alignItems: 'flex-end'
                      }}>
                        {asset.assetType === 'video' && videoProgress ? (
                          <div style={{ 
                            padding: '8px 12px',
                            background: '#e6f2ff',
                            borderRadius: '8px',
                            minWidth: '120px'
                          }}>
                            <p style={{ 
                              margin: '0 0 2px 0', 
                              fontSize: '11px', 
                              color: '#0050b3'
                            }}>
                              Watch Progress
                            </p>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '16px', 
                              fontWeight: '700',
                              color: '#0050b3'
                            }}>
                              {videoProgress.percentWatched || 0}%
                            </p>
                            <p style={{ 
                              margin: '2px 0 0 0', 
                              fontSize: '10px', 
                              color: '#1890ff'
                            }}>
                              {formatDuration(videoProgress.watchedSeconds)} / {formatDuration(videoProgress.totalDuration)}
                            </p>
                          </div>
                        ) : asset.assetType === 'quiz' && quizSubmission ? (
                          <div style={{ 
                            padding: '8px 12px',
                            background: quizSubmission.score >= 70 ? '#c6f6d5' : '#fed7d7',
                            borderRadius: '8px'
                          }}>
                            <p style={{ 
                              margin: '0 0 2px 0', 
                              fontSize: '11px', 
                              color: quizSubmission.score >= 70 ? '#22543d' : '#742a2a'
                            }}>
                              Quiz Score
                            </p>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '18px', 
                              fontWeight: '700',
                              color: quizSubmission.score >= 70 ? '#22543d' : '#742a2a'
                            }}>
                              {quizSubmission.score}%
                            </p>
                            <p style={{ 
                              margin: '2px 0 0 0', 
                              fontSize: '10px', 
                              color: quizSubmission.score >= 70 ? '#276749' : '#9b2c2c'
                            }}>
                              {quizSubmission.correctAnswers} / {quizSubmission.totalQuestions}
                            </p>
                          </div>
                        ) : score !== null ? (
                          <div style={{ 
                            padding: '6px 12px',
                            background: '#e6f2ff',
                            borderRadius: '6px'
                          }}>
                            <p style={{ margin: 0, fontSize: '12px', color: '#0050b3' }}>
                              Score: {score}%
                            </p>
                          </div>
                        ) : null}
                        
                        {/* Asset Quiz Submissions (for note/markdown assets) */}
                        {assetQuizSubs.length > 0 && (
                          <div style={{ 
                            padding: '12px',
                            background: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid #fbbf24'
                          }}>
                            <p style={{ 
                              margin: '0 0 8px 0', 
                              fontSize: '11px', 
                              fontWeight: '600',
                              color: '#92400e'
                            }}>
                              📝 Quiz Attempts ({assetQuizSubs.length}/3)
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {assetQuizSubs.map((sub, idx) => (
                                <div 
                                  key={sub._id}
                                  onClick={() => handleViewQuizAttempt(sub._id)}
                                  style={{
                                    padding: '6px 8px',
                                    background: sub.score >= 70 ? '#d1fae5' : '#fee2e2',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateX(4px)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateX(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  <span style={{ 
                                    fontSize: '10px', 
                                    color: sub.score >= 70 ? '#065f46' : '#991b1b',
                                    fontWeight: '600'
                                  }}>
                                    Attempt #{sub.attemptNumber}
                                  </span>
                                  <span style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '700',
                                    color: sub.score >= 70 ? '#065f46' : '#991b1b'
                                  }}>
                                    {sub.score}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Toggle Completion Button */}
                        <button
                          onClick={() => handleToggleCompletion(asset._id, isAssetCompleted(asset))}
                          disabled={togglingAsset === asset._id}
                          style={{
                            padding: '6px 12px',
                            background: isAssetCompleted(asset) ? '#fed7d7' : '#c6f6d5',
                            color: isAssetCompleted(asset) ? '#742a2a' : '#22543d',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: togglingAsset === asset._id ? 'not-allowed' : 'pointer',
                            opacity: togglingAsset === asset._id ? 0.6 : 1,
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            if (togglingAsset !== asset._id) {
                              e.currentTarget.style.opacity = '0.8';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (togglingAsset !== asset._id) {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          {togglingAsset === asset._id ? (
                            '⏳ Updating...'
                          ) : isAssetCompleted(asset) ? (
                            '✗ Mark Incomplete'
                          ) : (
                            '✓ Mark Complete'
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Quiz Attempt Details Modal */}
      {showQuizModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={closeQuizModal}
        >
          <div 
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '2px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: '#ffffff',
              zIndex: 1,
              borderRadius: '16px 16px 0 0'
            }}>
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', color: '#1a202c' }}>
                  📝 Quiz Attempt Details
                </h2>
                {selectedQuizSubmission && !loadingQuizDetails && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#718096' }}>
                    Attempt #{selectedQuizSubmission.submission.attemptNumber} • 
                    Score: <span style={{ 
                      fontWeight: '700',
                      color: selectedQuizSubmission.submission.score >= 70 ? '#22543d' : '#991b1b'
                    }}>
                      {selectedQuizSubmission.submission.score}%
                    </span> • 
                    {selectedQuizSubmission.submission.correctAnswers} / {selectedQuizSubmission.submission.totalQuestions} correct
                  </p>
                )}
              </div>
              <button
                onClick={closeQuizModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#718096',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f7fafc';
                  e.currentTarget.style.color = '#1a202c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#718096';
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px' }}>
              {loadingQuizDetails ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#667eea', fontSize: '16px' }}>Loading quiz details...</p>
                </div>
              ) : selectedQuizSubmission ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {selectedQuizSubmission.questions.map((question, qIndex) => {
                    const userAnswer = selectedQuizSubmission.submission.answers.find(
                      a => a.questionIndex === qIndex
                    );
                    const isCorrect = userAnswer?.isCorrect;

                    return (
                      <div 
                        key={qIndex}
                        style={{
                          padding: '20px',
                          background: '#f7fafc',
                          borderRadius: '12px',
                          border: `2px solid ${isCorrect ? '#48bb78' : '#fc8181'}`
                        }}
                      >
                        {/* Question Header */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '16px'
                        }}>
                          <h3 style={{ 
                            margin: 0, 
                            fontSize: '16px', 
                            fontWeight: '600',
                            color: '#1a202c',
                            flex: 1
                          }}>
                            Question {qIndex + 1}
                          </h3>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '700',
                            background: isCorrect ? '#c6f6d5' : '#fed7d7',
                            color: isCorrect ? '#22543d' : '#742a2a'
                          }}>
                            {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                          </span>
                        </div>

                        {/* Question Text */}
                        <p style={{
                          margin: '0 0 16px 0',
                          fontSize: '15px',
                          color: '#2d3748',
                          lineHeight: '1.6'
                        }}>
                          {question.question}
                        </p>

                        {/* Answer Options */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {question.options.map((option, optIndex) => {
                            const isUserAnswer = userAnswer?.selectedAnswer === optIndex;
                            const isCorrectAnswer = question.correctAnswer === optIndex;
                            
                            let bgColor = '#ffffff';
                            let borderColor = '#e2e8f0';
                            let textColor = '#4a5568';
                            
                            if (isCorrectAnswer) {
                              bgColor = '#c6f6d5';
                              borderColor = '#48bb78';
                              textColor = '#22543d';
                            } else if (isUserAnswer && !isCorrect) {
                              bgColor = '#fed7d7';
                              borderColor = '#fc8181';
                              textColor = '#742a2a';
                            }

                            return (
                              <div
                                key={optIndex}
                                style={{
                                  padding: '12px 16px',
                                  background: bgColor,
                                  border: `2px solid ${borderColor}`,
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px'
                                }}
                              >
                                <span style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  background: borderColor,
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '13px',
                                  fontWeight: '700',
                                  flexShrink: 0
                                }}>
                                  {String.fromCharCode(65 + optIndex)}
                                </span>
                                <span style={{
                                  fontSize: '14px',
                                  color: textColor,
                                  fontWeight: (isUserAnswer || isCorrectAnswer) ? '600' : '400',
                                  flex: 1
                                }}>
                                  {option}
                                </span>
                                {isCorrectAnswer && (
                                  <span style={{ 
                                    fontSize: '11px', 
                                    fontWeight: '700',
                                    color: '#22543d',
                                    background: '#9ae6b4',
                                    padding: '4px 8px',
                                    borderRadius: '4px'
                                  }}>
                                    Correct Answer
                                  </span>
                                )}
                                {isUserAnswer && !isCorrectAnswer && (
                                  <span style={{ 
                                    fontSize: '11px', 
                                    fontWeight: '700',
                                    color: '#742a2a',
                                    background: '#fc8181',
                                    padding: '4px 8px',
                                    borderRadius: '4px'
                                  }}>
                                    Your Answer
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#718096' }}>No quiz details available</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '20px 24px',
              borderTop: '2px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              position: 'sticky',
              bottom: 0,
              background: '#ffffff',
              borderRadius: '0 0 16px 16px'
            }}>
              <button
                onClick={closeQuizModal}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentCourseProgress;
